import { db } from '../firebase';
import medicalKnowledge from '../data/medical_knowledge.json';
import { config } from '../config';

const OLLAMA_CHAT_API_URL = config.OLLAMA_URL;

// ============================================================================
// SYSTEM PROMPTS (Shared Logic)
// ============================================================================

const BASE_SYSTEM_PROMPT = `
You are a highly intelligent and empathetic medical assistant for PulseNote.
Your goal is to accurately identify the medical specialist based on patient symptoms, BUT prioritize safety and clarity above all.

INSTRUCTIONS:
1. **SAFETY FIRST (URGENT_EMERGENCY)**: 
   - Start with "URGENT_EMERGENCY" if user reports life-threatening symptoms.
2. **DIAGNOSE**:
   - Analyze symptoms and map to: Dermatologist, Orthopedist, Psychiatrist, etc.
   - If unsure (< 80%), recommend General Physician.
3. **FORMAT**:
   - Provide a clear explanation.
   - Output ~CARD~ block for UI.
   - Output ~SUGGESTIONS~ list.
`;

const FEW_SHOT_EN = `
User: "Chest pain and sweating"
Assistant: "URGENT_EMERGENCY. Potential heart attack. Call ER."

User: "Itchy rash on arm"
Assistant: "Analysis: Skin issue.
Diagnosis: Dermatologist.
~CARD~
Condition: Dermatitis
Severity: Low
Action: Consult Dermatologist
~END_CARD~
~SUGGESTIONS~
['Apply cream', 'Avoid scratching']"
`;

const DOCTOR_SYSTEM_PROMPT = `
You are Dr. Maverick, an expert Clinical AI Assistant.
- Context: Analyze patient records and medical guidelines.
- Output: Professional, concise, evidence-based advice.
- Safety: Flag red flags immediately.
- Anti-Hallucination: Do not invent drugs or history.
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const normalizeBanglish = (text) => {
    // Simple normalization for common Banglish terms
    const mappings = {
        "matha": "matha (HEAD)", "buk": "buk (CHEST)", "pet": "pet (STOMACH)",
        "jor": "jor (FEVER)", "thanda": "thanda (COLD)"
    };
    let clean = text.toLowerCase();
    Object.keys(mappings).forEach(k => {
        clean = clean.replace(new RegExp(`\\b${k}\\b`, 'gi'), mappings[k]);
    });
    return clean;
};

// ============================================================================
// PATIENT CHATBOT ENGINE
// ============================================================================

export const analyzeSymptoms = async (chatHistory, language = 'en', userProfile = null) => {
    const lastUserMessage = normalizeBanglish(chatHistory[chatHistory.length - 1]?.content || "");

    // Construct System Prompt
    let systemPrompt = BASE_SYSTEM_PROMPT;
    if (userProfile) {
        systemPrompt += `\nPATIENT CONTEXT: Name: ${userProfile.name}, Age: ${userProfile.age}, Allergies: ${userProfile.allergies || 'None'}`;
    }
    systemPrompt += `\n${FEW_SHOT_EN}`;

    // Prepare Messages
    const messages = [
        { role: "system", content: systemPrompt },
        ...chatHistory
    ];

    try {
        console.log("🚀 Sending to PulseNote Backend (Proxy):", config.AI_API_URL);

        // ALWAYS USE BACKEND PROXY (Port 5000)
        // This handles RAG, Tunneling, and Formatting
        const response = await fetch(config.AI_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                text: lastUserMessage,
                history: chatHistory,
                // Pass minimal context for Patient Bot
                patient_context: userProfile ? JSON.stringify(userProfile) : "Guest User"
            }),
        });

        if (!response.ok) {
            const errText = await response.text();
            throw new Error(`Backend Error: ${response.status} - ${errText}`);
        }

        const data = await response.json();
        return data.answer;

    } catch (error) {
        console.error("AI Service Error:", error);
        return "⚠️ Patient AI Error: Unable to connect to Brain. Please ensure backend is running.";
    }
};

// ============================================================================
// NETWORK HELPERS
// ============================================================================

const fetchWithRetry = async (url, options, retries = 3, backoff = 1000) => {
    try {
        console.log(`🌐 Fetching: ${url} (Attempt ${4 - retries}/3)`);
        const response = await fetch(url, options);
        if (!response.ok) {
            const txt = await response.text();
            throw new Error(`Server responded with ${response.status}: ${txt}`);
        }
        return response;
    } catch (error) {
        if (retries > 0) {
            console.warn(`⚠️ Fetch failed. Retrying in ${backoff}ms...`, error);
            await new Promise(resolve => setTimeout(resolve, backoff));
            return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw error;
    }
};

// ============================================================================
// DOCTOR CO-PILOT ENGINE
// ============================================================================

export const analyzePatientHistory = async (chatHistory, patientData, visits) => {

    // 1. Construct Patient Context
    const recentVisits = visits.map(v => {
        const dateStr = v.date?.seconds ? new Date(v.date.seconds * 1000).toLocaleDateString() : (v.date || 'N/A');
        return `- ${dateStr}: ${v.diagnosis || 'Unknown'} (${v.symptoms || 'No symptoms listed'})`;
    }).join('\n');

    const patientContextStr = `
    PATIENT DETAILS:
    - Name: ${patientData.name}
    - Age: ${patientData.age} | Gender: ${patientData.gender}
    - Blood Group: ${patientData.bloodGroup || 'Unknown'}
    - Condition: ${patientData.condition || 'N/A'}
    - Allergies: ${patientData.allergies || 'NONE'}
    - Current Meds: ${patientData.currentMedications || 'None'}
    
    RECENT VISITS:
    ${recentVisits}
    `;

    // 2. Send to Backend
    const lastUserMessage = chatHistory[chatHistory.length - 1]?.content || "";

    try {
        console.log("🚀 Sending to PulseNote Backend (Doctor):", config.AI_API_URL);
        const response = await fetchWithRetry(config.AI_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                text: lastUserMessage,
                patient_context: patientContextStr,
                history: chatHistory
            }),
        });

        if (!response.ok) {
            const errText = await response.text();
            throw new Error(`Backend Error: ${response.status} - ${errText}`);
        }

        const data = await response.json();
        return data.answer;

    } catch (error) {
        console.error("Doctor AI Error:", error);
        return `⚠️ System Error: Unable to analyze patient data. (${error.message})`;
    }
};

// ============================================================================
// VOICE TRANSCRIPTION ENGINE
// ============================================================================

export const transcribeAudio = async (audioBlob, language = '') => {
    try {
        const formData = new FormData();
        formData.append('file', audioBlob);
        if (language) formData.append('language', language);

        // Assume /transcribe is on the same base URL as AI_API_URL
        // config.AI_API_URL is typically ".../query", so we strip suffix
        const baseUrl = config.AI_API_URL.replace('/query', '');
        console.log(`🎙️ Sending Transcription Request to: ${baseUrl}/transcribe`);

        const response = await fetchWithRetry(`${baseUrl}/transcribe`, {
            method: 'POST',
            body: formData
        });

        const data = await response.json();
        return data; // Expected { text: "..." }

    } catch (error) {
        console.error("Transcription Error:", error);
        throw error; // Propagate to UI for alerting
    }
};
